// src/components/GoogleAuthenticatorSettings.jsx
import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useTranslation } from 'react-i18next';
import GoogleAuthenticatorSetup from './GoogleAuthenticatorSetup';
import './GoogleAuthenticatorSettings.css';

const GoogleAuthenticatorSettings = ({ user }) => {
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  useEffect(() => {
    loadUserData();
  }, [user]);

  const loadUserData = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        setUserData(userDoc.data());
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnableTOTP = () => {
    setShowSetup(true);
  };

  const handleSetupComplete = () => {
    setShowSetup(false);
    loadUserData(); // Reload user data to show updated status
  };

  const handleSetupCancel = () => {
    setShowSetup(false);
  };

  const handleDisableTOTP = async () => {
    try {
      // Use backup wrapper for TOTP disable
      const { updateUserProfileWithBackup } = await import('../utils/clientBackupWrapper');
      
      await updateUserProfileWithBackup(user.uid, {
        totpEnabled: false,
        totpSecret: null,
        totpBackupCodes: null,
        totpSetupDate: null
      }, 'security_update', {
        field: 'totp_disable',
        disabledDate: new Date().toISOString()
      });
      
      setUserData(prev => ({
        ...prev,
        totpEnabled: false,
        totpSecret: null,
        totpBackupCodes: null,
        totpSetupDate: null
      }));
      
      setShowDisableConfirm(false);
    } catch (error) {
      console.error('Error disabling TOTP:', error);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="totp-settings">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (showSetup) {
    return (
      <GoogleAuthenticatorSetup
        user={user}
        onSetupComplete={handleSetupComplete}
        onCancel={handleSetupCancel}
      />
    );
  }

  return (
    <div className="totp-settings-simple">
      <div className="totp-status">
        <span className="totp-label">Two-Factor Authentication</span>
        <span className={`totp-status-text ${userData?.totpEnabled ? 'enabled' : 'disabled'}`}>
          {userData?.totpEnabled ? 'ON' : 'OFF'}
        </span>
      </div>
      
      <div className="totp-actions">
        {!userData?.totpEnabled ? (
          <button onClick={handleEnableTOTP} className="totp-enable-btn">
            Enable 2FA
          </button>
        ) : (
          <button 
            onClick={() => setShowDisableConfirm(true)} 
            className="totp-disable-btn"
          >
            Disable 2FA
          </button>
        )}
      </div>

      {showDisableConfirm && (
        <div className="totp-disable-modal">
          <div className="totp-modal-content">
            <h3>Disable Two-Factor Authentication?</h3>
            <p>This will remove the extra security from your account.</p>
            <div className="totp-modal-actions">
              <button 
                onClick={() => setShowDisableConfirm(false)} 
                className="totp-cancel-btn"
              >
                Cancel
              </button>
              <button 
                onClick={handleDisableTOTP} 
                className="totp-confirm-btn"
              >
                Disable
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleAuthenticatorSettings;
